// Lightweight client-side session logger with ring buffer + sessionStorage mirror
export type LogLevel = "info" | "warn" | "error" | "debug";

export interface LogEntry {
  ts: number;
  level: LogLevel;
  category: string;
  message: string;
  meta?: Record<string, unknown>;
}

const MAX_ENTRIES = 500;
const STORAGE_KEY = "ledlogic_session_log_v1";
const SYNC_DEBOUNCE_MS = 250;

class SessionLogger {
  private entries: LogEntry[] = [];
  private syncTimer: number | null = null;
  private listeners = new Set<() => void>();

  constructor() {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) this.entries = parsed.slice(-MAX_ENTRIES);
      }
    } catch {
      // ignore
    }
  }

  log(level: LogLevel, category: string, message: string, meta?: Record<string, unknown>) {
    const entry: LogEntry = {
      ts: Date.now(),
      level,
      category,
      message: typeof message === "string" ? message.slice(0, 1000) : String(message),
      meta: meta ? this.sanitizeMeta(meta) : undefined,
    };
    this.entries.push(entry);
    if (this.entries.length > MAX_ENTRIES) {
      this.entries.splice(0, this.entries.length - MAX_ENTRIES);
    }
    this.scheduleSync();
    this.listeners.forEach((l) => l());
  }

  info(category: string, message: string, meta?: Record<string, unknown>) {
    this.log("info", category, message, meta);
  }
  warn(category: string, message: string, meta?: Record<string, unknown>) {
    this.log("warn", category, message, meta);
  }
  error(category: string, message: string, meta?: Record<string, unknown>) {
    this.log("error", category, message, meta);
  }

  getEntries(): LogEntry[] {
    return [...this.entries];
  }

  count(): number {
    return this.entries.length;
  }

  clear() {
    this.entries = [];
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      // ignore
    }
    this.listeners.forEach((l) => l());
  }

  subscribe(fn: () => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  exportAsText(header?: Record<string, string>): string {
    const lines: string[] = [];
    lines.push("=== LEDLogic Session Log ===");
    if (header) {
      Object.entries(header).forEach(([k, v]) => lines.push(`${k}: ${v}`));
    }
    lines.push(`Generated: ${new Date().toISOString()}`);
    lines.push(`Entries: ${this.entries.length}`);
    lines.push("============================");
    lines.push("");
    for (const e of this.entries) {
      const time = new Date(e.ts).toISOString().split("T")[1].replace("Z", "");
      const meta = e.meta && Object.keys(e.meta).length > 0 ? " " + JSON.stringify(e.meta) : "";
      lines.push(`[${time}] [${e.level.toUpperCase()}] [${e.category}] ${e.message}${meta}`);
    }
    return lines.join("\n");
  }

  private sanitizeMeta(meta: Record<string, unknown>): Record<string, unknown> {
    const blocked = /password|token|secret|api[_-]?key|authorization|email/i;
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(meta)) {
      if (blocked.test(k)) {
        out[k] = "[redacted]";
      } else if (typeof v === "string") {
        out[k] = v.length > 300 ? v.slice(0, 300) + "…" : v;
      } else if (v === null || typeof v === "number" || typeof v === "boolean") {
        out[k] = v;
      } else {
        try {
          out[k] = JSON.parse(JSON.stringify(v));
        } catch {
          out[k] = "[unserializable]";
        }
      }
    }
    return out;
  }

  private scheduleSync() {
    if (this.syncTimer !== null) return;
    this.syncTimer = window.setTimeout(() => {
      this.syncTimer = null;
      try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(this.entries));
      } catch {
        // quota exceeded — drop oldest half and retry once
        try {
          this.entries = this.entries.slice(-Math.floor(MAX_ENTRIES / 2));
          sessionStorage.setItem(STORAGE_KEY, JSON.stringify(this.entries));
        } catch {
          // give up silently
        }
      }
    }, SYNC_DEBOUNCE_MS);
  }
}

export const sessionLogger = new SessionLogger();

// Convenience wrapper for app-level events
export function logEvent(category: string, message: string, meta?: Record<string, unknown>) {
  sessionLogger.info(category, message, meta);
}
